import React from 'react';
import { Spin, Popover, Input } from 'antd';
import s from './s.module.less';
import { allergySearch } from 'api/textVisit';
import { TAllergy } from 'types/common';

type AllergiesSelectProps = {
    value?: TAllergy[];
    onChange?: (val: TAllergy[]) => void;
};

const AllergiesSelect = (Props: AllergiesSelectProps) => {
    const { value, onChange } = Props;
    const [searchValue, setSearchValue] = React.useState<string>("");
    const [loading, setLoading] = React.useState(false);
    const latestSearchValue = React.useRef<string>("");
    const latestSearchTime = React.useRef<number>(0);
    const timer = React.useRef<NodeJS.Timeout | null>(null);
    const [specificArray, setSpecificArray] = React.useState<TAllergy[]>([]);
    const [ingredientArray, setIngredientArray] = React.useState<TAllergy[]>([]);
    const [classArray, setClassArray] = React.useState<TAllergy[]>([]);
    const [allValues, setAllValues] = React.useState<TAllergy[]>([]);
    const [mobileSelectType, setMobileSelectType] = React.useState<"specific" | "ingredient" | "class">("specific");

    let targetItem: TAllergy[] = [];
    if (mobileSelectType === "specific") {
        targetItem = specificArray;
    } else if (mobileSelectType === "ingredient") {
        targetItem = ingredientArray;
    } else {
        targetItem = classArray;
    }

    React.useEffect(() => {
        const doSearch = async () => {
            if (searchValue === latestSearchValue.current) {
                return;
            }
            latestSearchValue.current = searchValue;
            setLoading(true);
            const res = await allergySearch(searchValue);
            if (!res.error) {
                const allData: TAllergy[] = res.data?.data || [];
                const _specificArray = allData.filter((item) => `${item.allergyType}` === "1" || `${item.allergyType}` === "2");
                const _ingredientArray = allData.filter((item) => `${item.allergyType}` === "0");
                const _classArray = allData.filter((item) => `${item.allergyType}` === "3");
                setSpecificArray(_specificArray);
                setIngredientArray(_ingredientArray);
                setClassArray(_classArray);
                setAllValues(allData);
            } else {
                setSpecificArray([]);
                setIngredientArray([]);
                setClassArray([]);
                setAllValues([]);
            }
            setLoading(false);
        };
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
        if (searchValue) {
            timer.current = setTimeout(() => {
                doSearch();
            }, 500);
        } else {
            setAllValues([]);
            setSpecificArray([]);
            setIngredientArray([]);
            setClassArray([]);
        }
    }, [searchValue]);

    const handleSelect = (item: TAllergy) => {
        if (!value) {
            onChange?.([item]);
        } else if (!value?.some((v) => v.descr === item.descr)) {
            onChange?.([...(value || []), item]);
        }
        setSearchValue("");
        setAllValues([]);
        setSpecificArray([]);
        setIngredientArray([]);
        setClassArray([]);
    };

    const handleRemove = (item: TAllergy) => {
        if (!value) {
            return;
        }
        onChange?.([...(value || []).filter((v) => v.descr !== item.descr)]);
    };

    return (
        <div className={s.alWrap}>
            <Popover
                placement="bottomLeft"
                rootClassName={s.alPop}
                title={null}
                open={!!searchValue}
                content={
                    <div className={s.alPopWrap}>
                        {
                            loading ? (
                                <div className={s.loading}>
                                    <Spin />
                                </div>
                            ) : (
                                <div className={s.result}>
                                    <h5>Select any class, ingredient or specific drug allergy below</h5>
                                    <div className={s.alContent}>
                                        <div className={s.alCol}>
                                            <div className={s.colLabel}>Specific Drug ({specificArray.length})</div>
                                            <div className={s.colList}>
                                                {
                                                    specificArray.map((item) => (
                                                        <div
                                                            onClick={() => handleSelect(item)}
                                                            className={s.colItem}
                                                            key={item.descr}
                                                        >
                                                            {item.descr}
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                        <div className={s.alCol}>
                                            <div className={s.colLabel}>Ingredient ({ingredientArray.length})</div>
                                            <div className={s.colList}>
                                                {
                                                    ingredientArray.map((item) => (
                                                        <div
                                                            onClick={() => handleSelect(item)}
                                                            className={s.colItem}
                                                            key={item.descr}
                                                        >
                                                            {item.descr}
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                        <div className={s.alCol}>
                                            <div className={s.colLabel}>Class ({classArray.length})</div>
                                            <div className={s.colList}>
                                                {
                                                    classArray.map((item) => (
                                                        <div
                                                            onClick={() => handleSelect(item)}
                                                            className={s.colItem}
                                                            key={item.descr}
                                                        >
                                                            {item.descr}
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                    </div>
                }
                arrow={false}
            >
                <Input
                    size="large"
                    value={searchValue}
                    onChange={(e) => {
                        setSearchValue(e.target.value);
                        const hasVal = e.target.value;
                        if (hasVal) {
                            setLoading(true);
                        }
                    }}
                    placeholder="List any medication, food, or other allergies..."
                    className={s.allergies}
                />
            </Popover>
            <div className={s.valWrap}>
                {
                    value?.map((item) => (
                        <div className={s.valItem} key={item.descr}>
                            {item.descr}
                            <span
                                onClick={() => handleRemove(item)}
                                className={s.remove}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M6 18L18 6M6 6L18 18" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </div>
                    ))
                }
            </div>
        </div>
    );
};

export default AllergiesSelect;